import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Background,
  BaseEdge,
  Controls,
  getSmoothStepPath,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useNodesState,
  type Edge,
  type EdgeMarker,
  type EdgeProps,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { FlowGraph, FlowGraphNode } from '../api/contracts';
import { useTheme } from '../app/theme';
import { systemLabel } from '../lib/catalogs';
import {
  compareFlowSteps,
  FLOW_CARD_HEIGHT,
  FLOW_CARD_WIDTH,
  flowTopologyKey,
  layoutFlowGraphByOrientation,
  routeMidpoint,
  type FlowDirection,
  type FlowLayout,
  type FlowLayouts,
  type FlowOrientation,
  type FlowPoint,
} from '../lib/flow-layout';
import { IconLayoutHorizontal, IconLayoutVertical } from './icons';

/**
 * El grafo de una corrida, dibujado.
 *
 * Vive en un componente aparte porque se usa en dos lugares con la misma forma:
 * embebido en el evento —donde responde "donde estoy parado"— y en la pantalla
 * completa del flujo. Duplicar el dibujo en los dos habria garantizado que uno
 * de los dos se quedara atras.
 */

/**
 * Colores por estado del paso.
 *
 * Salen de las variables de `styles/global.css`, que ya tienen su version para
 * modo oscuro: un color escrito a mano aca se veria bien en claro y quedaria
 * ilegible en oscuro.
 */
const STATUS_STYLE: Record<string, { border: string; background: string; text: string }> = {
  ok: { border: 'var(--ok)', background: 'var(--ok-soft)', text: 'var(--ok)' },
  partial: { border: 'var(--warn)', background: 'var(--warn-soft)', text: 'var(--warn)' },
  failed: { border: 'var(--danger)', background: 'var(--danger-soft)', text: 'var(--danger)' },
  quarantined: { border: 'var(--warn)', background: 'var(--warn-soft)', text: 'var(--warn)' },
  running: { border: 'var(--accent)', background: 'var(--accent-soft)', text: 'var(--accent)' },
  pending: {
    border: 'var(--line-strong)',
    background: 'var(--surface-sunken)',
    text: 'var(--text-muted)',
  },
};

/**
 * Color de cada tipo de conector.
 *
 * Se fija explicito —en vez de dejar el gris por defecto de React Flow— porque
 * la punta de flecha se dibuja en un `<marker>` SVG aparte del trazo: si cada
 * uno toma su color de un lado distinto, terminan sin coincidir en cuanto
 * alguien cambia un tema. Aca los dos leen esta misma tabla.
 */
const EDGE_COLOR: Record<string, string> = {
  observed: 'var(--text-muted)',
  inferred: 'var(--text-muted)',
  expected: 'var(--line-strong)',
};

/** Lo que cada caja necesita saber de si misma. */
interface StepNodeData extends Record<string, unknown> {
  readonly step: FlowGraphNode;
  /** El paso del evento que se esta mirando. Se dibuja marcado. */
  readonly current: boolean;
}

/**
 * Caja de un paso.
 *
 * Es un nodo propio y no el default de React Flow porque el default acepta un
 * solo texto plano: aca hacen falta tres niveles —sistema, evento y cuantas
 * veces corrio— y con un `\n` en un `label` el navegador los colapsa en una
 * linea ilegible.
 */
function StepNode({ data }: NodeProps): ReactElement {
  const { step, current } = data as unknown as StepNodeData;
  const style = STATUS_STYLE[step.status] ?? STATUS_STYLE['pending'];
  return (
    <div
      className={`flow-step${(step.processingCount ?? 0) > 0 ? ' flow-step--processing' : ''}`}
      // La nota de un resultado esperado se lee al pasar el mouse; el estado no
      // cambia: un paso con nota es un paso que salio bien.
      title={step.note ?? undefined}
      style={{
        width: FLOW_CARD_WIDTH,
        height: FLOW_CARD_HEIGHT,
        border: `${current ? 2 : 1}px ${step.status === 'pending' ? 'dashed' : 'solid'} ${
          style?.border ?? 'var(--line)'
        }`,
        // El paso que se esta mirando se marca con un halo y no cambiando su
        // color: el color ya significa estado, y pisarlo esconderia que el paso
        // fallo justo en la caja que el operador vino a mirar.
        boxShadow: current ? '0 0 0 3px var(--accent-soft)' : undefined,
        background: style?.background ?? 'transparent',
        color: style?.text ?? 'inherit',
      }}
    >
      <Handle id="left" type="target" position={Position.Left} />
      <Handle id="top" type="target" position={Position.Top} />
      <div className="flow-step__system">
        {systemLabel(step.system)}
        {current ? <span className="flow-step__current">Estás aquí</span> : null}
      </div>
      <div className="flow-step__label" title={step.label}>
        {step.label}
      </div>
      <div className="flow-step__meta">
        {step.status === 'pending'
          ? step.optional
            ? 'Paso opcional'
            : 'Aún no ocurrió'
          : `${step.messageCount} ${step.messageCount === 1 ? 'mensaje' : 'mensajes'}`}
        {step.failedCount > 0 ? ` · ${step.failedCount} con error` : ''}
        {step.note === null || step.note === undefined ? '' : ' · con nota'}
      </div>
      <div className="flow-step__activity">
        {(step.processingCount ?? 0) > 0 ? (
          <span>
            <span className="flow-step__spinner" aria-hidden="true" /> {step.processingCount}{' '}
            procesando
          </span>
        ) : null}
        {(step.waitingCount ?? 0) > 0 ? (
          <span>
            {(step.processingCount ?? 0) > 0 ? ' · ' : ''}
            {step.waitingCount} en espera
          </span>
        ) : null}
      </div>
      <div className="flow-step__status">
        <span className="flow-step__status-dot" aria-hidden="true" />
        {step.status === 'pending' ? 'Pendiente' : null}
        {step.status === 'running' ? 'En curso' : null}
        {step.status === 'ok' ? 'Completado' : null}
        {step.status === 'partial' ? 'Con errores parciales' : null}
        {step.status === 'failed' ? 'Fallido' : null}
        {step.status === 'quarantined' ? 'En cuarentena' : null}
      </div>
      <Handle id="right" type="source" position={Position.Right} />
      <Handle id="bottom" type="source" position={Position.Bottom} />
    </div>
  );
}

const NODE_TYPES = { step: StepNode };

/** Use the routed segments, including detours around intermediate cards. */
function RoutedEdge(props: EdgeProps): ReactElement {
  const points = props.data?.['points'] as FlowPoint[] | undefined;
  const [fallback, labelX, labelY] = getSmoothStepPath(props);
  const path = points?.length
    ? points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
    : fallback;
  const middle = points ? routeMidpoint(points) : undefined;
  return (
    <BaseEdge
      id={props.id}
      path={path}
      markerEnd={props.markerEnd ?? ''}
      style={props.style}
      label={props.label}
      labelX={middle?.x ?? labelX}
      labelY={middle?.y ?? labelY}
      labelStyle={props.labelStyle ?? {}}
      labelBgStyle={props.labelBgStyle ?? {}}
      labelBgPadding={props.labelBgPadding ?? [4, 2]}
    />
  );
}

const EDGE_TYPES = { routed: RoutedEdge };

/** El ancho donde el lienzo pasa a ser alto y angosto: el de un telefono. */
const NARROW_SCREEN_QUERY = '(max-width: 600px)';

/**
 * Orientación con que arranca el dibujo: vertical en un telefono, donde un
 * flujo en fila solo cabe achicado hasta no leerse. Se decide al montar; despues
 * manda el operador. Sin `matchMedia` (un navegador viejo), horizontal.
 */
function initialOrientation(): FlowOrientation {
  const narrow =
    typeof window.matchMedia === 'function' && window.matchMedia(NARROW_SCREEN_QUERY).matches;
  return narrow ? 'vertical' : 'horizontal';
}

export interface FlowGraphCanvasProps {
  readonly graph: FlowGraph;
  /** Id del paso que corresponde al evento abierto, si se mira desde uno. */
  readonly currentNodeId?: string | null;
  /** Paso cuya ficha esta abierta. Manda sobre `currentNodeId` para el foco. */
  readonly selectedNodeId?: string | null;
  readonly onSelectStep: (step: FlowGraphNode) => void;
  readonly height?: number;
}

export function FlowGraphCanvas(props: FlowGraphCanvasProps): ReactElement {
  return <RunGraphCanvas key={props.graph.correlationId} {...props} />;
}

function RunGraphCanvas({
  graph,
  currentNodeId = null,
  selectedNodeId = null,
  onSelectStep,
  height = 520,
}: FlowGraphCanvasProps): ReactElement {
  const { resolvedTheme } = useTheme();
  const [hovered, setHovered] = useState<string | null>(null);
  // La orientación es la FORMA del dibujo (ver `layoutFlowGraphByOrientation`);
  // el sentido de las flechas sale del dibujo elegido.
  const [orientation, setOrientation] = useState<FlowOrientation>(initialOrientation);
  const [instance, setInstance] = useState<ReactFlowInstance | null>(null);
  const [layoutsResult, setLayoutsResult] = useState<{ key: string; value: FlowLayouts } | null>(
    null,
  );
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const manuallyMoved = useRef(new Set<string>());
  const topologyKey = flowTopologyKey(graph);
  const layout = layoutsResult?.key === topologyKey ? layoutsResult.value[orientation] : null;
  const direction: FlowDirection =
    layout?.direction ?? (orientation === 'horizontal' ? 'RIGHT' : 'DOWN');
  const loading = layout === null && layoutError !== topologyKey;
  const focusNodeId = hovered ?? selectedNodeId ?? currentNodeId;

  // Los dos dibujos se calculan juntos, una vez por topología: cambiar de
  // orientación es instantáneo y el polling no vuelve a ordenar nada.
  useEffect(() => {
    let cancelled = false;
    setLayoutError(null);
    void layoutFlowGraphByOrientation(topologyKey).then(
      (value) => {
        if (!cancelled) setLayoutsResult({ key: topologyKey, value });
      },
      () => {
        // Keep the steps available in a simple list if the lazy chunk fails.
        if (!cancelled) setLayoutError(topologyKey);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [topologyKey, retry]);

  const computed = useMemo(
    () => toFlowNodes(graph, currentNodeId, layout),
    [graph, currentNodeId, layout],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(computed);
  useEffect(() => {
    setNodes((previous) => mergeFlowNodes(previous, computed, manuallyMoved.current));
  }, [computed, setNodes]);

  // Once a card is moved, use live connectors until the operator reorders.
  // Keeping the old routes could cut across a card moved into another branch.
  const routesValid =
    layout !== null &&
    nodes.every((node) => {
      const position = layout.positions.get(node.id);
      return position?.x === node.position.x && position.y === node.position.y;
    });
  const edges = useMemo(
    () => toFlowEdges(graph, focusNodeId, direction, routesValid ? layout : null),
    [graph, focusNodeId, direction, routesValid, layout],
  );

  const fitGraph = useCallback(() => {
    void instance?.fitView({ padding: 0.12, minZoom: 0.1, maxZoom: 1 });
  }, [instance]);
  const centerStep = useCallback(() => {
    const step =
      instance?.getNode(selectedNodeId ?? currentNodeId ?? '') ?? instance?.getNodes()[0];
    if (step) {
      void instance?.setCenter(
        step.position.x + FLOW_CARD_WIDTH / 2,
        step.position.y + FLOW_CARD_HEIGHT / 2,
        { zoom: 1 },
      );
    }
  }, [instance, selectedNodeId, currentNodeId]);
  // Keep the current step and its immediate dependencies in view when the full
  // graph would make the labels unreadable. Polling must not reset this view.
  const anchorRef = useRef({ graph, id: selectedNodeId ?? currentNodeId });
  anchorRef.current = { graph, id: selectedNodeId ?? currentNodeId };
  useEffect(() => {
    if (loading || !instance) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      void instance.fitView({ padding: 0.12, minZoom: 0.1, maxZoom: 1 }).then(() => {
        // Large graphs start at a readable scale. "Ver todo" offers the overview.
        if (!cancelled && instance.getViewport().zoom < 0.65) {
          const anchor = anchorRef.current.id ?? instance.getNodes()[0]?.id;
          const neighborhood = new Set([anchor]);
          for (const edge of anchorRef.current.graph.edges) {
            if (edge.source === anchor || edge.target === anchor) {
              neighborhood.add(edge.source);
              neighborhood.add(edge.target);
            }
          }
          void instance.fitView({
            nodes: instance.getNodes().filter((node) => neighborhood.has(node.id)),
            padding: 0.12,
            minZoom: 0.65,
            maxZoom: 1,
          });
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [loading, instance, layout]);

  const handleNodeClick = useCallback(
    (_event: unknown, node: Node) => {
      const step = graph.nodes.find((item) => item.id === node.id);
      if (step !== undefined) onSelectStep(step);
    },
    [graph, onSelectStep],
  );

  const reorder = (): void => {
    manuallyMoved.current.clear();
    setNodes(computed);
    if (layoutError !== null) setRetry((value) => value + 1);
    requestAnimationFrame(fitGraph);
  };

  return (
    <div className="flow-graph">
      <div className="flow-graph__toolbar">
        <div className="flow-graph__orientation" role="group" aria-label="Orientación del flujo">
          {(['horizontal', 'vertical'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className="button"
              aria-pressed={orientation === value}
              onClick={() => {
                if (orientation === value) return;
                manuallyMoved.current.clear();
                setOrientation(value);
              }}
            >
              {value === 'horizontal' ? (
                <IconLayoutHorizontal size={16} />
              ) : (
                <IconLayoutVertical size={16} />
              )}
              {value === 'horizontal' ? 'Horizontal' : 'Vertical'}
            </button>
          ))}
        </div>
        <div className="flow-graph__actions">
          <button type="button" className="button" onClick={reorder} disabled={loading}>
            Reordenar
          </button>
          <button type="button" className="button" onClick={fitGraph} disabled={loading}>
            Ver todo
          </button>
          <button type="button" className="button" onClick={centerStep} disabled={loading}>
            {selectedNodeId || currentNodeId ? 'Centrar paso' : 'Ir al inicio'}
          </button>
        </div>
      </div>
      {layoutError === topologyKey ? (
        <p role="alert" className="muted">
          No se pudo ordenar el diagrama. Usa Reordenar para volver a intentar.
        </p>
      ) : null}
      <div className="flow-graph__canvas" style={{ height }} aria-busy={loading}>
        {loading ? (
          <div className="flow-graph__loading" role="status">
            Ordenando el flujo…
          </div>
        ) : null}
        <ReactFlow
          colorMode={resolvedTheme}
          style={{ visibility: loading ? 'hidden' : 'visible' }}
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          onInit={setInstance}
          onNodeClick={handleNodeClick}
          onNodeMouseEnter={(_event, node) => setHovered(node.id)}
          onNodeMouseLeave={() => setHovered(null)}
          onNodesChange={(changes) => {
            for (const change of changes) {
              if (change.type === 'position' && change.position)
                manuallyMoved.current.add(change.id);
            }
            onNodesChange(changes);
          }}
          minZoom={0.1}
          maxZoom={1.5}
          nodesDraggable
          nodesConnectable={false}
          edgesReconnectable={false}
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} size={1} color="var(--line-strong)" />
          <Controls
            showInteractive={false}
            fitViewOptions={{ padding: 0.12, minZoom: 0.1, maxZoom: 1 }}
          />
        </ReactFlow>
      </div>
      <p className="flow-graph__hint">
        Arrastra el fondo para recorrer el flujo.{' '}
        {/* En una pantalla tactil no hay "pasar sobre": el CSS esconde esta parte. */}
        <span className="flow-graph__hint-hover">
          Pasa sobre un paso para destacar sus conexiones.{' '}
        </span>
        Ver todo muestra la corrida completa.
      </p>
    </div>
  );
}

/** Que significa cada tipo de flecha. Sin esto, lo deducido se lee como certeza. */
export function FlowGraphLegend(): ReactElement {
  return (
    <span className="flow-graph__legend">
      <span>
        <i className="flow-graph__line" aria-hidden="true" />
        Causalidad registrada
      </span>
      <span>
        <i className="flow-graph__line flow-graph__line--inferred" aria-hidden="true" />
        Orden temporal
      </span>
      <span>
        <i className="flow-graph__line flow-graph__line--expected" aria-hidden="true" />
        Paso esperado
      </span>
    </span>
  );
}

/**
 * Traduce el grafo del contrato a lo que React Flow dibuja.
 *
 * Se parte en nodos y aristas porque cambian por motivos distintos: las cajas
 * solo cuando cambia la corrida, las lineas ademas cada vez que se mueve el
 * foco. Recalcular las cajas en cada hover haria que React Flow las remonte.
 */
export function toReactFlow(
  graph: FlowGraph | null,
  currentNodeId: string | null = null,
  focusNodeId: string | null = null,
): { nodes: Node[]; edges: Edge[] } {
  return {
    nodes: toFlowNodes(graph, currentNodeId),
    edges: toFlowEdges(graph, focusNodeId),
  };
}

export function toFlowNodes(
  graph: FlowGraph | null,
  currentNodeId: string | null = null,
  layout: FlowLayout | null = null,
): Node[] {
  if (graph === null) {
    return [];
  }

  return [...graph.nodes].sort(compareFlowSteps).map((node, index) => ({
    id: node.id,
    type: 'step',
    position: layout?.positions.get(node.id) ?? { x: 24, y: 24 + index * (FLOW_CARD_HEIGHT + 48) },
    data: { step: node, current: node.id === currentNodeId },
    // El tamaño va explicito para que el encuadre inicial no calcule con las
    // medidas por defecto de React Flow y deje el grafo corrido y diminuto.
    style: { width: FLOW_CARD_WIDTH, height: FLOW_CARD_HEIGHT },
    width: FLOW_CARD_WIDTH,
    height: FLOW_CARD_HEIGHT,
  }));
}

/**
 * Las lineas, con las del paso en foco destacadas.
 *
 * QUE RESUELVE: en una corrida con diez pasos las lineas se cruzan, y seguir con
 * la vista cual entra y cual sale de una caja es justamente lo que cuesta. Con
 * un paso en foco, sus conectores se engrosan y el resto se atenua, asi que
 * "que lo gatillo y que gatillo el" se lee de un vistazo.
 *
 * El foco NO cambia el tipo de linea: una deducida sigue punteada aunque este
 * destacada. Lo que la distingue de una causalidad registrada no es cuanto
 * resalta, y perder esa diferencia al pasar el mouse convertiria una suposicion
 * en un hecho.
 */
export function toFlowEdges(
  graph: FlowGraph | null,
  focusNodeId: string | null = null,
  direction: FlowDirection = 'RIGHT',
  layout: FlowLayout | null = null,
): Edge[] {
  if (graph === null) {
    return [];
  }

  return graph.edges.map((edge) => {
    const incident =
      focusNodeId !== null && (edge.source === focusNodeId || edge.target === focusNodeId);
    const dimmed = focusNodeId !== null && !incident;
    const color = incident ? 'var(--accent)' : EDGE_COLOR[edge.kind];

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: direction === 'RIGHT' ? 'right' : 'bottom',
      targetHandle: direction === 'RIGHT' ? 'left' : 'top',
      type: 'routed',
      data: { points: layout?.routes.get(edge.id) },
      // OJO CON `animated`: React Flow lo dibuja como guiones en movimiento, o sea
      // punteado. Marcar asi la arista respaldada por la causalidad la volvia
      // indistinguible de la deducida, que es exactamente lo que la leyenda
      // promete distinguir. La real va solida y mas gruesa; punteadas van las
      // otras dos.
      animated: false,
      // La punta marca el sentido: sin ella, "A causo B" y "B causo A" se dibujan
      // igual y hay que deducir la direccion del orden de lectura, que en la fila
      // que vuelve hacia la izquierda es justamente al reves.
      markerEnd: arrowFor(color),
      // Las destacadas se dibujan encima de las atenuadas, si no un cruce las
      // tapa justo donde hay que mirar.
      zIndex: incident ? 10 : 0,
      style: {
        stroke: color,
        strokeDasharray:
          edge.kind === 'observed' ? undefined : edge.kind === 'inferred' ? '6 4' : '2 4',
        strokeWidth: (edge.kind === 'observed' ? 2 : 1) + (incident ? 1 : 0),
        opacity: dimmed ? 0.4 : edge.kind === 'expected' ? 0.6 : 1,
      },
      label:
        edge.kind === 'observed' && edge.messageCount > 1 ? `×${edge.messageCount}` : undefined,
      // React Flow pinta el fondo de la etiqueta de blanco fijo, que sobre el
      // fondo oscuro queda como una pastilla brillante en medio del grafo.
      labelBgStyle: { fill: 'var(--surface-raised)' },
      labelStyle: { fill: 'var(--text-muted)', fontSize: 11 },
      labelBgPadding: [4, 2] as [number, number],
    };
  });
}

/**
 * Punta de flecha del conector.
 *
 * Va rellena y no como dos lineas: a los tamaños en que se ve el grafo completo,
 * una punta abierta de 8px se confunde con el trazo y deja de leerse la
 * direccion, que es para lo unico que esta. Recibe el color ya resuelto de su
 * linea —incluido el del resaltado— para que trazo y punta nunca discrepen.
 */
function arrowFor(color: string | undefined): EdgeMarker {
  return {
    type: MarkerType.ArrowClosed,
    width: 18,
    height: 18,
    markerUnits: 'userSpaceOnUse',
    color: color ?? 'var(--line-strong)',
  };
}

/** Keep the operator's layout while refreshing status and counters. */
export function mergeFlowNodes(
  previous: Node[],
  next: Node[],
  preservePositions?: ReadonlySet<string>,
): Node[] {
  const byId = new Map(previous.map((node) => [node.id, node]));
  return next.map((node) => {
    const existing = byId.get(node.id);
    return existing
      ? {
          ...existing,
          ...node,
          position:
            preservePositions === undefined || preservePositions.has(node.id)
              ? existing.position
              : node.position,
          selected: existing.selected ?? false,
        }
      : node;
  });
}
